// Graphics for "The Art of Passing" masterclass.
//
// <PassingGraphic name="..."/> renders one hand-drawn SVG panel. Everything
// is inline SVG so it scales clean on retina and prints fine; CSS-keyframe
// animation is scoped per graphic. Dark-theme aware via the C color object.
//
// Organized to match the module: the perception-action LOOP (see it → choose
// it → deliver it) plus the subcategory panels inside each stage.
//
// No em dashes. Plain language.

import React from "react";
import { GOLD } from "../constants/theme";

const OFF = "#60A5FA";   // offense / the passer
const DEF = "#F87171";   // defense
const OPEN = "#34D399";  // open / good / advantage
const BALL = "#F5A623";  // the ball

// ---- shared primitives -----------------------------------------------------

const Frame = ({ children, label, vb = "0 0 320 200" }) => (
  <svg viewBox={vb} style={{ width: "100%", height: "auto" }} role="img" aria-label={label}>
    {children}
  </svg>
);

// A faint half-court reference (baseline arc + lane) some diagrams sit on.
const CourtArc = ({ C, cx = 160, cy = 30, r = 120 }) => (
  <g stroke={C.textDim} strokeWidth="1.5" fill="none" opacity="0.35">
    <path d={`M${cx - r} ${cy} A ${r} ${r} 0 0 0 ${cx + r} ${cy}`} />
  </g>
);

const Player = ({ x, y, color, r = 9, fill = false, label }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill={fill ? color : "none"} stroke={color} strokeWidth="2.5" />
    {label && <text x={x} y={y + 4} textAnchor="middle" fontSize="10" fontWeight="800" fill={fill ? "#0A0A0A" : color}>{label}</text>}
  </g>
);

const Ball = ({ x, y, r = 5 }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill={BALL} />
    <path d={`M${x - r} ${y} h${r * 2} M${x} ${y - r} v${r * 2}`} stroke="#0A0A0A" strokeWidth="1" opacity="0.6" />
  </g>
);

const Arrow = ({ x1, y1, x2, y2, color, dash, w = 2.5, id }) => {
  const mid = `arrow-${id}`;
  return (
    <g>
      <defs>
        <marker id={mid} markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={color} />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={w} strokeDasharray={dash} markerEnd={`url(#${mid})`} strokeLinecap="round" />
    </g>
  );
};

const Cap = ({ x, y, text, color, anchor = "middle" }) => (
  <text x={x} y={y} textAnchor={anchor} fontSize="10.5" fontWeight="700" fill={color}>{text}</text>
);

// ===========================================================================
// THE LOOP (recurring motif)
// ===========================================================================
function Loop({ C, active }) {
  const nodes = [
    { key: "see", label: "SEE IT", sub: "perceive", x: 60, y: 100 },
    { key: "choose", label: "CHOOSE IT", sub: "decide", x: 160, y: 45 },
    { key: "deliver", label: "DELIVER IT", sub: "execute", x: 260, y: 100 },
  ];
  return (
    <Frame vb="0 0 320 175" label="The passing loop: see it, choose it, deliver it, and a new option appears">
      <style>{`@keyframes pl-flow{0%{stroke-dashoffset:60}100%{stroke-dashoffset:0}}`}</style>
      {/* arcs between nodes */}
      <path d="M78 88 Q110 55 145 52" fill="none" stroke={GOLD} strokeWidth="2.5" strokeDasharray="5 5" style={{ animation: "pl-flow 1.4s linear infinite" }} markerEnd="" />
      <path d="M175 52 Q210 55 242 88" fill="none" stroke={GOLD} strokeWidth="2.5" strokeDasharray="5 5" style={{ animation: "pl-flow 1.4s linear infinite" }} />
      <path d="M248 118 Q160 178 72 118" fill="none" stroke={OPEN} strokeWidth="2.5" strokeDasharray="5 5" style={{ animation: "pl-flow 1.8s linear infinite" }} />
      <Cap x={160} y={150} text="a new window opens" color={OPEN} />
      {nodes.map(n => {
        const on = active === n.key;
        return (
          <g key={n.key}>
            <circle cx={n.x} cy={n.y} r="30" fill={on ? `${GOLD}22` : C.cardGradient} stroke={on ? GOLD : C.cardBorder} strokeWidth={on ? 3 : 1.5} />
            <text x={n.x} y={n.y - 2} textAnchor="middle" fontSize="11" fontWeight="900" fill={on ? GOLD : C.text}>{n.label}</text>
            <text x={n.x} y={n.y + 11} textAnchor="middle" fontSize="8.5" fill={C.textDim} letterSpacing="0.5">{n.sub}</text>
          </g>
        );
      })}
    </Frame>
  );
}

// ===========================================================================
// SEE IT
// ===========================================================================

// Peripheral vision: eyes on one thing, court map held in the periphery.
function VisionField({ C }) {
  return (
    <Frame label="Central gaze on one spot while peripheral vision holds the whole floor">
      <CourtArc C={C} />
      <Player x={160} y={150} color={OFF} fill label="1" />
      {/* peripheral cone */}
      <path d="M160 150 L40 55 A150 150 0 0 1 280 55 Z" fill={GOLD} opacity="0.06" />
      <path d="M160 150 L40 55 M160 150 L280 55" stroke={GOLD} strokeWidth="1" strokeDasharray="3 4" opacity="0.5" />
      {/* teammates scattered, all "seen" */}
      {[[70, 70], [250, 72], [120, 60], [210, 95]].map(([x, y], i) => <Player key={i} x={x} y={y} color={C.textDim} r={7} />)}
      {/* central gaze locked on ONE (a decoy look) */}
      <Arrow x1={160} y1={142} x2={112} y2={66} color={C.text} id="gaze" w={2} dash="none" />
      <Cap x={92} y={58} text="eyes here" color={C.text} />
      <Cap x={160} y={172} text="whole floor held in the periphery" color={C.textDim} />
    </Frame>
  );
}

// Two speeds of pattern recognition.
function PatternTwoSpeeds({ C }) {
  return (
    <Frame vb="0 0 320 175" label="Fast intuitive reads versus slow deliberate coverage reads">
      <rect x="12" y="20" width="140" height="135" rx="12" fill={OPEN} opacity="0.07" stroke={`${OPEN}66`} />
      <rect x="168" y="20" width="140" height="135" rx="12" fill={OFF} opacity="0.07" stroke={`${OFF}66`} />
      <Cap x={82} y={42} text="FAST" color={OPEN} />
      <Cap x={238} y={42} text="SLOW" color={OFF} />
      <text x={82} y={62} textAnchor="middle" fontSize="9" fill={C.textDim}>in the moment</text>
      <text x={238} y={62} textAnchor="middle" fontSize="9" fill={C.textDim}>between possessions</text>
      <text x={82} y={92} textAnchor="middle" fontSize="9.5" fill={C.text}>no thinking</text>
      <text x={82} y={110} textAnchor="middle" fontSize="9.5" fill={C.text}>react to the picture</text>
      <text x={82} y={128} textAnchor="middle" fontSize="9.5" fill={C.text}>trained by reps</text>
      <text x={238} y={92} textAnchor="middle" fontSize="9.5" fill={C.text}>"they hard hedge"</text>
      <text x={238} y={110} textAnchor="middle" fontSize="9.5" fill={C.text}>narrow to 2-3 options</text>
      <text x={238} y={128} textAnchor="middle" fontSize="9.5" fill={C.text}>then let fast take over</text>
    </Frame>
  );
}

// Timing: lead the target. The Rob Gray "car crossing the street."
function TimingLead({ C }) {
  return (
    <Frame label="Lead a moving teammate: throw to where they will be, not where they are">
      <Player x={60} y={150} color={OFF} fill label="1" />
      {/* cutter now, and where they'll be */}
      <Player x={150} y={120} color={C.textDim} r={8} />
      <Player x={250} y={70} color={OPEN} r={8} />
      <Arrow x1={158} y1={116} x2={244} y2={74} color={C.textDim} dash="4 4" w={1.8} id="run" />
      <Cap x={150} y={140} text="now" color={C.textDim} />
      <Cap x={258} y={64} text="the spot" color={OPEN} anchor="start" />
      {/* the pass leads to the spot */}
      <Arrow x1={70} y1={144} x2={240} y2={74} color={GOLD} id="lead" w={2.5} dash="none" />
      <Cap x={160} y={98} text="lead line = speed × angle" color={GOLD} />
    </Frame>
  );
}

// Reading momentum: the stunt vs the real dig.
function ReadMomentum({ C }) {
  const Fig = ({ x, tilt, color, label, foot }) => (
    <g>
      <g transform={`translate(${x},0) rotate(${tilt} ${0} 130)`} stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round">
        <circle cx="0" cy="70" r="8" fill={color} stroke="none" />
        <line x1="0" y1="78" x2="0" y2="112" />
        <line x1="0" y1="88" x2="-14" y2="100" />
        <line x1="0" y1="88" x2="14" y2="100" />
        <line x1="0" y1="112" x2={foot === "L" ? -18 : -6} y2="148" strokeWidth={foot === "L" ? 4 : 2.5} />
        <line x1="0" y1="112" x2={foot === "R" ? 18 : 6} y2="148" strokeWidth={foot === "R" ? 4 : 2.5} />
      </g>
      <Cap x={x} y={168} text={label} color={color} />
    </g>
  );
  return (
    <Frame vb="0 0 320 180" label="A stunt versus a real dig: read which leg the defender commits weight into">
      <Fig x={95} tilt={-4} color={C.textDim} label="stunt (fake)" foot="R" />
      <Fig x={225} tilt={-13} color={DEF} label="real dig (committed)" foot="L" />
      <Cap x={225} y={40} text="weight into the leg" color={DEF} />
      <Cap x={95} y={40} text="feet still even" color={C.textDim} />
    </Frame>
  );
}

// Coverages funnel: many options collapse to a few once you read the scheme.
function CoveragesFunnel({ C }) {
  return (
    <Frame vb="0 0 320 180" label="Reading the coverage collapses many options down to two or three">
      {/* fan of many options */}
      {Array.from({ length: 7 }).map((_, i) => {
        const a = -60 + i * 20;
        const rad = (a * Math.PI) / 180;
        return <line key={i} x1="40" y1="90" x2={40 + 70 * Math.cos(rad)} y2={90 + 70 * Math.sin(rad)} stroke={C.textDim} strokeWidth="1.5" opacity="0.5" />;
      })}
      <Cap x={40} y={30} text="many options" color={C.textDim} />
      {/* funnel */}
      <path d="M120 40 L180 78 L180 102 L120 140 Z" fill={GOLD} opacity="0.08" stroke={`${GOLD}66`} />
      <Cap x={150} y={94} text="read the" color={GOLD} />
      <Cap x={150} y={106} text="coverage" color={GOLD} />
      {/* 3 clean options out */}
      {[74, 90, 106].map((y, i) => <Arrow key={i} x1={182} y1={90} x2={270} y2={y} color={OPEN} id={`o${i}`} w={2.5} dash="none" />)}
      <Cap x={276} y={90} text="2-3" color={OPEN} anchor="start" />
    </Frame>
  );
}

// ===========================================================================
// CHOOSE IT
// ===========================================================================

// No-look vs look-off.
function LookoffVsNolook({ C }) {
  return (
    <Frame vb="0 0 320 190" label="No-look throws to nobody; a look-off holds a defender to spring a real 2 on 1">
      {/* left: no-look, bad */}
      <text x={80} y={26} textAnchor="middle" fontSize="10.5" fontWeight="900" fill={DEF}>NO-LOOK</text>
      <Player x={40} y={110} color={OFF} fill label="1" />
      <Player x={120} y={75} color={C.textDim} r={7} />
      <Arrow x1={48} y1={104} x2={110} y2={80} color={C.textDim} id="nl-gaze" dash="3 3" w={1.6} />
      <Cap x={120} y={62} text="looks at nobody" color={C.textDim} />
      <text x={80} y={165} textAnchor="middle" fontSize="9.5" fill={DEF}>a good defender knows the map</text>
      <line x1="160" y1="30" x2="160" y2="170" stroke={C.cardBorder} strokeWidth="1" />
      {/* right: look-off, good */}
      <text x={240} y={26} textAnchor="middle" fontSize="10.5" fontWeight="900" fill={OPEN}>LOOK-OFF</text>
      <Player x={195} y={120} color={OFF} fill label="1" />
      <Player x={300} y={120} color={OPEN} r={7} label="" />
      <Player x={250} y={60} color={C.textDim} r={7} />
      <Player x={255} y={95} color={DEF} r={7} />
      {/* eyes to the wrong man, pass to the corner */}
      <Arrow x1={202} y1={114} x2={244} y2={66} color={C.text} id="lo-gaze" dash="3 3" w={1.6} />
      <Arrow x1={204} y1={122} x2={290} y2={120} color={GOLD} id="lo-pass" w={2.5} dash="none" />
      <Cap x={250} y={148} text="holds the help, springs the 2v1" color={OPEN} />
    </Frame>
  );
}

// Gravity: scoring threat opens the gap.
function Gravity({ C }) {
  return (
    <Frame label="Being a real scoring threat pulls a defender in and opens the passing gap">
      <radialGradient id="grav" cx="30%" cy="55%">
        <stop offset="0%" stopColor={OFF} stopOpacity="0.25" />
        <stop offset="100%" stopColor={OFF} stopOpacity="0" />
      </radialGradient>
      <ellipse cx="90" cy="110" rx="80" ry="60" fill="url(#grav)" />
      <Player x={90} y={110} color={OFF} fill label="1" />
      <Cap x={90} y={150} text="threat to score" color={OFF} />
      {/* defender pulled in */}
      <Arrow x1={190} y1={90} x2={130} y2={104} color={DEF} id="pull" dash="5 4" w={2} />
      <Player x={200} y={86} color={DEF} r={8} />
      {/* the gap that opens */}
      <Player x={270} y={130} color={OPEN} r={8} />
      <Arrow x1={104} y1={116} x2={262} y2={130} color={GOLD} id="grav-pass" w={2.5} dash="none" />
      <Cap x={250} y={158} text="the gap opens" color={OPEN} />
    </Frame>
  );
}

// Window optimization: one dribble too many closes the pass.
function WindowOptimize({ C }) {
  const Bar = ({ y, label, color, closeAt }) => (
    <g>
      <Cap x={14} y={y - 8} text={label} color={color} anchor="start" />
      <rect x={14} y={y} width="292" height="14" rx="7" fill={C.cardGradient} stroke={C.cardBorder} />
      <rect x={14} y={y} width={closeAt} height="14" rx="7" fill={OPEN} opacity="0.5" />
      <rect x={14 + closeAt} y={y} width={292 - closeAt} height="14" rx="7" fill={DEF} opacity="0.4" />
      <line x1={14 + closeAt} y1={y - 3} x2={14 + closeAt} y2={y + 17} stroke={C.text} strokeWidth="2" />
    </g>
  );
  return (
    <Frame vb="0 0 320 175" label="The passing window is open then closes; an extra dribble misses it">
      <Cap x={160} y={22} text="the window is open, then it closes" color={C.textDim} />
      <Bar y={55} label="pass on time" color={OPEN} closeAt={150} />
      <text x={175} y={65} fontSize="9" fill={C.text}>ball's already gone</text>
      <Bar y={110} label="one dribble too many" color={DEF} closeAt={150} />
      <text x={175} y={120} fontSize="9" fill={DEF}>closed. picked up. stuck.</text>
      <Cap x={160} y={162} text="good passers throw before the extra beat" color={GOLD} />
    </Frame>
  );
}

// ===========================================================================
// DELIVER IT
// ===========================================================================

// Pass taxonomy grid.
function PassTaxonomy({ C }) {
  const items = ["Chest", "Bounce", "Skip", "Behind-back", "Lob", "Outlet", "Touch", "Baseball", "Jump", "Wrap", "Push", "Pocket"];
  return (
    <Frame vb="0 0 320 210" label="The vocabulary of passes a complete passer owns">
      <Cap x={160} y={18} text="the vocabulary" color={GOLD} />
      {items.map((t, i) => {
        const col = i % 4, row = Math.floor(i / 4);
        const x = 20 + col * 74, y = 34 + row * 56;
        return (
          <g key={t}>
            <rect x={x} y={y} width="66" height="44" rx="9" fill={C.cardGradient} stroke={C.cardBorder} />
            <circle cx={x + 33} cy={y + 17} r="7" fill={BALL} opacity="0.9" />
            <text x={x + 33} y={y + 37} textAnchor="middle" fontSize="9" fontWeight="700" fill={C.text}>{t}</text>
          </g>
        );
      })}
    </Frame>
  );
}

// Touch = perceive (where + force) + execute (place it).
function TouchTwoParts({ C }) {
  return (
    <Frame vb="0 0 320 170" label="Touch is two parts: perceiving where and how much force, then placing it">
      <circle cx="80" cy="80" r="42" fill={OFF} opacity="0.08" stroke={`${OFF}66`} />
      <Cap x={80} y={54} text="PERCEIVE" color={OFF} />
      <text x={80} y={78} textAnchor="middle" fontSize="9" fill={C.text}>where to put it</text>
      <text x={80} y={94} textAnchor="middle" fontSize="9" fill={C.text}>how much force</text>
      <Arrow x1={128} y1={80} x2={188} y2={80} color={GOLD} id="touch" w={2.5} dash="none" />
      <text x={158} y={72} textAnchor="middle" fontSize="16">＋</text>
      <circle cx="238" cy="80" r="42" fill={OPEN} opacity="0.08" stroke={`${OPEN}66`} />
      <Cap x={238} y={54} text="EXECUTE" color={OPEN} />
      <text x={238} y={78} textAnchor="middle" fontSize="9" fill={C.text}>place it exactly</text>
      <text x={238} y={94} textAnchor="middle" fontSize="9" fill={C.text}>into his pocket</text>
      <Cap x={160} y={148} text="lose either half and the pass is off" color={C.textDim} />
    </Frame>
  );
}

// Friction and spin: how the ball behaves off the floor.
function FrictionSpin({ C }) {
  const Path = ({ x, d, label, color, dash }) => (
    <g>
      <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeDasharray={dash} />
      <Cap x={x} y={40} text={label} color={color} />
    </g>
  );
  return (
    <Frame vb="0 0 320 175" label="Angle and spin change how a pass behaves off the floor">
      <line x1="14" y1="135" x2="306" y2="135" stroke={C.textDim} strokeWidth="2" opacity="0.5" />
      {/* low pass dies */}
      <Path x={58} label="low = dies" color={DEF} d="M20 90 Q50 130 90 132" dash="none" />
      {/* downward bounces up */}
      <Path x={160} label="down = bounces" color={OPEN} d="M120 70 L150 135 Q158 135 158 128 L200 90" dash="none" />
      {/* forward skids */}
      <Path x={262} label="forward = skids" color={GOLD} d="M220 90 Q250 135 260 133 T306 120" dash="4 3" />
      <Cap x={160} y={162} text="read the friction and the angle" color={C.textDim} />
    </Frame>
  );
}

// Momentum override: cancel your drift so the ball doesn't sail.
function MomentumOverride({ C }) {
  return (
    <Frame label="Moving one way, throw a touch behind it so the ball does not sail">
      <Player x={110} y={110} color={OFF} fill label="1" />
      {/* body momentum */}
      <Arrow x1={122} y1={110} x2={210} y2={110} color={C.textDim} id="body" dash="5 4" w={2} />
      <Cap x={165} y={100} text="body drifts →" color={C.textDim} />
      {/* naive throw sails */}
      <Arrow x1={122} y1={104} x2={280} y2={60} color={DEF} id="sail" dash="3 3" w={1.8} />
      <Cap x={250} y={50} text="sails" color={DEF} />
      {/* corrected: throw behind */}
      <Arrow x1={120} y1={116} x2={250} y2={140} color={OPEN} id="fix" w={2.5} dash="none" />
      <Cap x={230} y={158} text="throw behind = on target" color={OPEN} />
    </Frame>
  );
}

// Power chain: no-dip "only the elbow" vs whole-body.
function PowerChain({ C }) {
  const Fig = ({ x, chain, label, color }) => (
    <g>
      <g stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round">
        <circle cx={x} cy="55" r="8" fill={color} stroke="none" />
        <line x1={x} y1="63" x2={x} y2="100" />
        <line x1={x} y1="100" x2={x - 12} y2="140" strokeWidth={chain ? 4 : 2} />
        <line x1={x} y1="100" x2={x + 12} y2="140" strokeWidth={chain ? 4 : 2} />
        <line x1={x} y1="74" x2={x + 20} y2="70" strokeWidth={chain ? 2.5 : 4} />
      </g>
      {chain
        ? [["knees", 132], ["hips", 100], ["core", 86], ["arm", 72]].map(([t, y], i) =>
            <circle key={i} cx={x + 24} cy={y} r="3" fill={OPEN} />)
        : <circle cx={x + 24} cy={72} r="4" fill={DEF} />}
      <Cap x={x} y={162} text={label} color={color} />
    </g>
  );
  return (
    <Frame vb="0 0 320 178" label="Arm-only passing leaks power; a linked chain from the ground drives it">
      <Fig x={90} chain={false} label="arm only (leak)" color={DEF} />
      <Cap x={140} y={72} text="just the elbow" color={DEF} anchor="start" />
      <Fig x={235} chain={true} label="linked chain (power)" color={OPEN} />
      <Cap x={266} y={110} text="ground → arm" color={OPEN} anchor="start" />
    </Frame>
  );
}

// Create the lane: triangles not lines, and the angle dribble.
function CreateLane({ C }) {
  return (
    <Frame vb="0 0 320 190" label="Off ball make a triangle not a line; on ball take an angle to open the lane">
      {/* line = blocked */}
      <text x={80} y={24} textAnchor="middle" fontSize="10" fontWeight="800" fill={DEF}>LINE (blocked)</text>
      <Player x={40} y={90} color={OFF} fill label="1" />
      <Player x={80} y={90} color={DEF} r={8} />
      <Player x={120} y={90} color={C.textDim} r={7} />
      <line x1="49" y1="90" x2="112" y2="90" stroke={DEF} strokeWidth="2" strokeDasharray="4 3" />
      <text x={80} y={120} textAnchor="middle" fontSize="18" fill={DEF}>✕</text>
      <line x1="160" y1="30" x2="160" y2="175" stroke={C.cardBorder} strokeWidth="1" />
      {/* triangle = open */}
      <text x={240} y={24} textAnchor="middle" fontSize="10" fontWeight="800" fill={OPEN}>TRIANGLE (open)</text>
      <Player x={195} y={100} color={OFF} fill label="1" />
      <Player x={235} y={95} color={DEF} r={8} />
      <Player x={285} y={135} color={OPEN} r={7} />
      <Arrow x1={198} y1={112} x2={216} y2={150} color={C.textDim} id="angle" dash="4 3" w={1.6} />
      <Cap x={196} y={168} text="angle dribble" color={C.textDim} anchor="start" />
      <Arrow x1={206} y1={104} x2={278} y2={132} color={GOLD} id="tri-pass" w={2.5} dash="none" />
    </Frame>
  );
}

// ===========================================================================
// KEEP IT SHARP
// ===========================================================================

// Touch erodes fast, returns fast with calibration.
function ErosionCalibration({ C }) {
  return (
    <Frame vb="0 0 320 175" label="Fine touch fades quickly without reps and comes back quickly with calibration">
      <line x1="30" y1="140" x2="306" y2="140" stroke={C.textDim} strokeWidth="1.5" opacity="0.5" />
      <line x1="30" y1="30" x2="30" y2="140" stroke={C.textDim} strokeWidth="1.5" opacity="0.5" />
      <Cap x={30} y={22} text="touch" color={C.textDim} anchor="start" />
      {/* decay then quick recovery */}
      <path d="M30 45 Q90 120 150 128" fill="none" stroke={DEF} strokeWidth="2.5" />
      <path d="M150 128 Q175 60 210 48" fill="none" stroke={OPEN} strokeWidth="2.5" />
      <path d="M210 48 L300 44" fill="none" stroke={OPEN} strokeWidth="2.5" strokeDasharray="4 3" />
      <Cap x={95} y={100} text="fades fast" color={DEF} />
      <Cap x={185} y={78} text="calibrates back fast" color={OPEN} />
      <Cap x={160} y={165} text="so touch has to be touched often" color={C.textDim} />
    </Frame>
  );
}

// System efficiency: tuned vs untuned motor system.
function EfficiencySystem({ C }) {
  const Chain = ({ y, tuned, label, color }) => (
    <g>
      <Cap x={14} y={y - 10} text={label} color={color} anchor="start" />
      {[0, 1, 2, 3, 4].map(i => {
        const x = 30 + i * 55;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="9" fill="none" stroke={color} strokeWidth="2.5" opacity={tuned ? 1 : 0.55} />
            {i < 4 && <line x1={x + 9} y1={y} x2={x + 46} y2={y} stroke={color} strokeWidth="2.5" strokeDasharray={tuned ? "none" : "3 4"} opacity={tuned ? 1 : 0.55} />}
          </g>
        );
      })}
    </g>
  );
  return (
    <Frame vb="0 0 320 170" label="A tuned motor system moves as one smooth chain; an untuned one leaks energy" >
      <Chain y={55} tuned={true} label="tuned: one smooth chain" color={OPEN} />
      <Chain y={120} tuned={false} label="untuned: leaks energy every move" color={DEF} />
      <Cap x={160} y={152} text="fluidity is efficiency you can see" color={GOLD} />
    </Frame>
  );
}

// ---- dispatcher ------------------------------------------------------------

const GRAPHICS = {
  loop: (p) => <Loop {...p} />,
  "vision-field": VisionField,
  "pattern-two-speeds": PatternTwoSpeeds,
  "timing-lead": TimingLead,
  "read-momentum": ReadMomentum,
  "coverages-funnel": CoveragesFunnel,
  "lookoff-vs-nolook": LookoffVsNolook,
  gravity: Gravity,
  "window-optimize": WindowOptimize,
  "pass-taxonomy": PassTaxonomy,
  "touch-two-parts": TouchTwoParts,
  "friction-spin": FrictionSpin,
  "momentum-override": MomentumOverride,
  "power-chain": PowerChain,
  "create-lane": CreateLane,
  "erosion-calibration": ErosionCalibration,
  "efficiency-system": EfficiencySystem,
};

export function PassingGraphic({ name, caption, active, C }) {
  const G = GRAPHICS[name];
  if (!G) return null;
  return (
    <figure style={{ margin: "8px 0" }}>
      <G C={C} active={active} />
      {caption && (
        <figcaption style={{ fontSize: 12, color: C.textDim, fontStyle: "italic", lineHeight: 1.5, marginTop: 4, textAlign: "center" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
